<template>
  <div class="race-results">
    <div class="race-results__header">
      <h3>Results</h3>
    </div>
    <div class="race-results__content">
      <div 
        v-for="result in results" 
        :key="result.roundIndex" 
        class="race-results__round"
      >
        <div class="race-results__round-header">
          {{ result.roundNumber }}{{ getOrdinalSuffix(result.roundNumber) }} Lap - {{ result.distance }}m
        </div>
        <table class="race-results__table">
          <thead>
            <tr>
              <th>Position</th>
              <th>Name</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="entry in result.results" 
              :key="entry.horseId"
              :class="getPositionClass(entry.position)"
            >
              <td>{{ entry.position }}</td>
              <td>
                <span 
                  class="horse-name"
                  :style="{ borderLeftColor: entry.horseColor.hex }"
                >
                  {{ entry.horseName }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div v-if="results.length === 0" class="race-results__empty">
        <p>Race results will appear here as each round completes</p>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { getOrdinalSuffix } from '@/utils/helpers'

export default {
  name: 'RaceResults',
  setup() {
    const store = useStore()
    const results = computed(() => store.getters['race/allResults'])
    
    function getPositionClass(pos) {
      if (pos === 1) return 'race-results__position--first'
      if (pos === 2) return 'race-results__position--second'
      if (pos === 3) return 'race-results__position--third'
      return ''
    }
    
    return { results, getPositionClass, getOrdinalSuffix }
  }
}
</script>

<style scoped>
.race-results {
  background-color: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.race-results__header {
  background: linear-gradient(135deg, var(--color-secondary) 0%, var(--color-secondary-hover) 100%);
  color: white;
  padding: var(--space-4) var(--space-5);
  text-align: center;
  flex-shrink: 0;
}

.race-results__header h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
}

.race-results__header h3::before {
  content: '🏆';
  font-size: 16px;
}

.race-results__content {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-3);
}

.race-results__round {
  margin-bottom: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.race-results__round:last-child {
  margin-bottom: 0;
}

.race-results__round-header {
  background-color: var(--color-secondary-light);
  padding: var(--space-2) var(--space-3);
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
  color: var(--color-secondary-hover);
}

.race-results__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.race-results__table th,
.race-results__table td {
  padding: var(--space-1) var(--space-2);
  text-align: center;
}

.race-results__table th {
  background-color: var(--color-border-light);
  font-weight: 600;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-secondary);
}

.race-results__table tbody tr {
  border-bottom: 1px solid var(--color-border-light);
  transition: all 0.15s ease;
}

.race-results__table tbody tr:last-child {
  border-bottom: none;
}

.race-results__table tbody tr:hover {
  background-color: var(--color-secondary-light);
}

.race-results__position--first {
  background: linear-gradient(90deg, #fef9c3 0%, #fef08a 100%) !important;
}

.race-results__position--first td:first-child {
  font-weight: 700;
  color: #ca8a04;
}

.race-results__position--second {
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 100%) !important;
}

.race-results__position--second td:first-child {
  font-weight: 600;
  color: #64748b;
}

.race-results__position--third {
  background: linear-gradient(90deg, #fed7aa 0%, #fdba74 100%) !important;
}

.race-results__position--third td:first-child {
  font-weight: 600;
  color: #c2410c;
}

.horse-name {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding-left: var(--space-2);
  border-left: 3px solid transparent;
}

.race-results__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  padding: var(--space-6);
  color: var(--color-text-muted);
}

.race-results__empty p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
}
</style>
